/* ============================================================
   Axiom Supply — Article template behaviours (docs/blog-seo.md)

   All progressive enhancement: the article reads fine with JS off.
     · gives every H2/H3 in the body a stable id  -> jump links Google
       can surface as sitelinks, and shareable deep links
     · builds the sticky table of contents (desktop rail + mobile
       <details>) from those headings, with scroll-spy
     · reading-progress bar
     · copy-link share button
     · wraps wide tables so they scroll instead of blowing out the layout
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function slugify(text) {
    return (text || '')
      .toLowerCase()
      .replace(/['’"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  class AxiomArticle extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-aa-content]');
      this.tocs = Array.prototype.slice.call(this.querySelectorAll('[data-aa-toc]'));
      this.progress = this.querySelector('[data-aa-progress]');

      if (this.content) {
        this.wrapTables();
        this.headings = this.collectHeadings();
        this.buildToc();
        this.spy();
      }
      this.trackProgress();
      this.bindShare();
    }

    /* ---- tables: keep wide spec tables from breaking mobile ---- */
    wrapTables() {
      var tables = this.content.querySelectorAll('table');
      for (var i = 0; i < tables.length; i++) {
        var t = tables[i];
        if (t.parentElement && t.parentElement.classList.contains('aa-tablewrap')) continue;
        var wrap = document.createElement('div');
        wrap.className = 'aa-tablewrap';
        t.parentNode.insertBefore(wrap, t);
        wrap.appendChild(t);
      }
    }

    /* ---- stable heading ids + hover anchor links ---- */
    collectHeadings() {
      var nodes = Array.prototype.slice.call(this.content.querySelectorAll('h2, h3'));
      var used = {};
      var out = [];

      nodes.forEach(function (el) {
        var text = (el.textContent || '').trim();
        if (!text) return;

        var id = el.id;
        if (!id) {
          id = slugify(text) || 'section';
          if (used[id]) {
            used[id] += 1;
            id = id + '-' + used[id];
          } else {
            used[id] = 1;
          }
          // Don't collide with something already on the page.
          while (document.getElementById(id)) id = id + '-x';
          el.id = id;
        }

        var anchor = document.createElement('a');
        anchor.className = 'aa-anchor';
        anchor.href = '#' + id;
        anchor.setAttribute('aria-label', 'Link to this section');
        anchor.textContent = '#';
        el.appendChild(anchor);

        out.push({ id: id, text: text, level: el.tagName === 'H3' ? 3 : 2, el: el });
      });

      return out;
    }

    /* ---- table of contents ---- */
    buildToc() {
      var headings = this.headings;
      var self = this;
      if (!headings.length || headings.length < 2) {
        this.tocs.forEach(function (list) {
          var host = list.closest('[data-aa-toc-host]');
          if (host) host.hidden = true;
        });
        return;
      }

      this.tocs.forEach(function (list) {
        list.innerHTML = '';
        headings.forEach(function (h) {
          var li = document.createElement('li');
          if (h.level === 3) li.className = 'is-sub';
          var a = document.createElement('a');
          a.href = '#' + h.id;
          a.textContent = h.text;
          a.dataset.aaTocLink = h.id;
          a.addEventListener('click', function (e) {
            var target = document.getElementById(h.id);
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
            history.replaceState(null, '', '#' + h.id);
            var details = a.closest('details');
            if (details) details.open = false;
          });
          li.appendChild(a);
          list.appendChild(li);
        });

        var host = list.closest('[data-aa-toc-host]');
        if (host) {
          host.hidden = false;
          host.setAttribute('data-has-items', '');
        }
        self.markActive(headings[0].id);
      });
    }

    markActive(id) {
      var links = this.querySelectorAll('[data-aa-toc-link]');
      for (var i = 0; i < links.length; i++) {
        links[i].classList.toggle('is-active', links[i].dataset.aaTocLink === id);
      }
    }

    spy() {
      if (!this.headings || !this.headings.length || !('IntersectionObserver' in window)) return;
      var self = this;
      var visible = new Set();

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visible.add(entry.target.id);
            else visible.delete(entry.target.id);
          });
          // Highlight the topmost heading that is (or last was) in view.
          var current = null;
          for (var i = 0; i < self.headings.length; i++) {
            var h = self.headings[i];
            if (visible.has(h.id)) {
              current = h.id;
              break;
            }
            if (h.el.getBoundingClientRect().top < 120) current = h.id;
          }
          if (current) self.markActive(current);
        },
        { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
      );

      this.headings.forEach(function (h) {
        self.observer.observe(h.el);
      });
    }

    /* ---- reading progress ---- */
    trackProgress() {
      if (!this.progress || !this.content) return;
      var self = this;
      var ticking = false;

      var update = function () {
        var box = self.content.getBoundingClientRect();
        var total = box.height - window.innerHeight;
        var done = total > 0 ? (-box.top / total) * 100 : box.top < 0 ? 100 : 0;
        self.progress.style.width = Math.min(100, Math.max(0, done)).toFixed(2) + '%';
        ticking = false;
      };

      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ---- copy link ---- */
    bindShare() {
      var btn = this.querySelector('[data-aa-copy]');
      if (!btn) return;
      var label = btn.querySelector('[data-aa-copy-label]') || btn;
      var original = label.textContent;

      btn.addEventListener('click', function () {
        var url = btn.dataset.aaCopy || window.location.href;
        var done = function () {
          label.textContent = 'Copied';
          setTimeout(function () {
            label.textContent = original;
          }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(function () {});
        } else {
          var tmp = document.createElement('input');
          tmp.value = url;
          document.body.appendChild(tmp);
          tmp.select();
          try {
            document.execCommand('copy');
            done();
          } catch (e) {
            /* no-op */
          }
          document.body.removeChild(tmp);
        }
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('axiom-article')) {
    customElements.define('axiom-article', AxiomArticle);
  }
})();
